/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { buildImportDonePresentation } from '../csv-core/domain/csvUiCopy';
import type { CsvFlowMode, CsvImportDoneCounts, CsvImportDoneLineTone } from '../csv-core/domain/csvUiCopy';
import { colors } from '../theme/public-api';
import { posSharedUi } from '../../shared/ui/posSharedUi';
import { csvImportExportModalStyles as styles } from './csvImportExportModal.styles';

function ImportDonePanel({
  counts,
  mode,
  hasErrorReport,
}: {
  counts: CsvImportDoneCounts;
  mode: CsvFlowMode;
  hasErrorReport: boolean;
}) {
  const presentation = buildImportDonePresentation(mode, counts);
  const isSuccess = counts.applied > 0 && counts.failed === 0;
  const isPartial = counts.applied > 0 && counts.failed > 0;
  const iconName = isSuccess ? 'checkmark-circle' : isPartial ? 'alert-circle' : 'close-circle';
  const iconColor = isSuccess ? colors.success : isPartial ? colors.warning : colors.danger;

  const lineToneStyle: Record<CsvImportDoneLineTone, { color: string } | null> = {
    default: null,
    muted: { color: colors.textMuted },
    success: { color: colors.success },
    error: { color: colors.danger },
  };

  return (
    <View style={styles.donePanel}>
      <View style={styles.doneHero}>
        <Ionicons name={iconName} size={40} color={iconColor} />
        <Text style={styles.doneHeadline}>{presentation.headline}</Text>
        <Text style={styles.doneDetail}>{presentation.detail}</Text>
      </View>
      {presentation.lines.length > 0 ? (
        <View style={styles.doneBreakdown}>
          {presentation.lines.map((line) => (
            <Text
              key={line.text}
              style={[styles.doneBreakdownLine, lineToneStyle[line.tone ?? 'default']]}
            >
              {line.text}
            </Text>
          ))}
        </View>
      ) : null}
      {hasErrorReport ? (
        <Text style={styles.doneFootnote}>Descargá el detalle para corregir el archivo.</Text>
      ) : null}
    </View>
  );
}

type CsvImportDoneStepProps = {
  counts: CsvImportDoneCounts;
  mode: CsvFlowMode;
  errorCount: number;
  onDownloadErrorReport: () => void;
  onImportAnother: () => void;
  onClose: () => void;
};

export const CsvImportDoneStep = ({
  counts,
  mode,
  errorCount,
  onDownloadErrorReport,
  onImportAnother,
  onClose,
}: CsvImportDoneStepProps) => (
  <>
    <ImportDonePanel counts={counts} mode={mode} hasErrorReport={errorCount > 0} />
    {errorCount > 0 ? (
      <TouchableOpacity style={posSharedUi.secondaryButton} onPress={onDownloadErrorReport}>
        <Text style={posSharedUi.secondaryButtonText}>Descargar errores ({errorCount})</Text>
      </TouchableOpacity>
    ) : null}
    <TouchableOpacity style={[posSharedUi.secondaryButton, styles.importBtn]} onPress={onImportAnother}>
      <Text style={posSharedUi.secondaryButtonText}>Importar otro</Text>
    </TouchableOpacity>
    <TouchableOpacity style={[posSharedUi.primaryButton, styles.importBtn]} onPress={onClose}>
      <Text style={posSharedUi.primaryButtonText}>Cerrar</Text>
    </TouchableOpacity>
  </>
);
